import { useRootStore } from '@/stores/root.store';
import Jwt from './jwt.model';
import User from './user.model';
import AccessToken from './access-token.model';

export default class Session {
  accessToken;
  jwt;

  constructor(jwt, accessToken) {
    this.jwt = jwt;
    this.accessToken = accessToken;
  }

  get isAuthenticated() {
    return !!this.jwt || !!this.accessToken;
  }

  get isTokenSession() {
    return !!this.accessToken;
  }

  get isJwtSession() {
    return !!this.jwt;
  }

  get isLive() {
    return this.isAuthenticated && !this.accessToken?.isSandbox;
  }

  get isPaper() {
    return this.isAuthenticated && !!this.accessToken && this.accessToken?.isSandbox;
  }

  async createUser() {
    if (!this.jwt && !this.accessToken) throw new Error('JWT/AccessToken required to create a user');

    // Note: the ordering here is important because if the JWT and access token exist,
    // the access token will take precedence over the JWT
    // this should only occur in situations where:
    // 1. the user has logged in manually via the /login?token... endpoint
    // 2. the user has logged into the sandbox environment

    // if the session has an access token, we need to create a user from the access token
    if (this.accessToken) return await User.fromProfile();

    // if the session has a JWT we already have everything we need for the user
    if (this.jwt) return User.fromJwt(this.jwt);
  }

  logout() {
    useRootStore().cookieService?.clearRedirectCookie();
    useRootStore().cookieService?.clearJwtFromCookie();
    useRootStore().cookieService?.clearAccessTokenCookie();
    useRootStore().cookieService?.clearEnvironmentCookie();
  }

  static loginFromJwt(jwt, expiry, saveToCookie = true) {
    if (!(jwt instanceof Jwt)) throw new Error('jwt must be an instance of Jwt');

    if (jwt) useRootStore().setupUserServices(jwt.token);
    if (jwt.hasAccessToken) useRootStore().setupTradierServices(jwt.accessToken);
    if (saveToCookie) useRootStore().cookieService.setJwtToCookie(jwt.token, expiry);
    return new Session(jwt);
  }

  static loginFromToken(token, environment, jwt = null) {
    if (!token) throw new Error('token must be provided');

    const accessToken = new AccessToken(token, environment);
    useRootStore().setupTradierServices(accessToken);
    useRootStore().cookieService?.setAccessTokenToCookie(accessToken.token);
    useRootStore().cookieService?.setEnvironmentToCookie(accessToken.environment);
    return new Session(jwt, accessToken);
  }

  static fromCookies() {
    const jwt = useRootStore().cookieService?.getJwtFromCookie();
    const accessToken = useRootStore().cookieService?.getAccessTokenFromCookie();
    const environment = useRootStore().cookieService?.getEnvironmentFromCookie();

    // if no JWT and no accessToken is present, no session can be created
    if (!jwt && !accessToken) return null;

    // user has entered their paper trading account, let them continue with that
    if (accessToken && environment && environment === 'sandbox') {
      return Session.loginFromToken(accessToken, environment, jwt);
    }

    // otherwise, we're trying to use production so we should prioritize the JWT over the access token
    if (jwt) return Session.loginFromJwt(new Jwt(jwt), null, false);
    if (accessToken) return Session.loginFromToken(accessToken, environment);
  }
}
